import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { LoadingIndicator, Toast, NotificationBadge } from '@ygtechnologies/bygr-frontend-component-library';
import { AlertBanner, Button, ButtonBar, Checkbox, Dialog } from '@ygtechnologies/rubus-ui';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { ResidentialUnitDeadline, ResidentialUnitGroupAndUnitDeadlines, ResidentialUnitGroupDeadline } from '@/types';
import {
    getComplexProjectDeadlines,
    saveComplexProjectDeadline,
    deleteComplexProjectDeadline,
    GetResidentialUnitsDeadlinesForProjectDeadline,
    revertUnitDeadlines,
} from '@/services/projects';
import ProjectSettingsComplexDeadlinesTable from './ProjectSettingsComplexDeadlinesTable';
import theme from './ProjectSettingsComplexDeadlines.module.scss';

interface ProjectSettingsComplexDeadlinesProps {
    onClose(): void;
    onAccept?(): void;
    show: boolean;
    projectId: number;
    projectDeadlineId: number | null;
}

const ProjectSettingsComplexDeadlines: React.FC<ProjectSettingsComplexDeadlinesProps> = ({
    show,
    onClose,
    onAccept,
    projectId,
    projectDeadlineId,
}) => {
    const [saving, setSaving] = useState(false);

    const [showFloors, setShowFloors] = useState(false);
    const [showUnits, setShowUnits] = useState(false);
    const [combinedDeadlines, setCombinedDeadlines] = useState<ResidentialUnitGroupAndUnitDeadlines | null>(null);

    const handleCancelClick = () => {
        triggerHandler(onClose);
        setShowFloors(false);
        setShowUnits(false);
    };

    const handleRevertRugClick = async (deadline: ResidentialUnitGroupDeadline) => {
        await deleteComplexProjectDeadline(deadline.projectId, deadline);
        //TODO: fix display
        Toast.success(<Trans>Reverted successfully</Trans>);
        await handleOpen();
    };
    const handleRevertRuClick = async (deadline: ResidentialUnitDeadline) => {
        if (combinedDeadlines === null || combinedDeadlines?.residentalUnitDeadlines === null) {
            return;
        }
        const unit = combinedDeadlines.residentalUnitDeadlines.find(
            (x) => x.name === deadline.name && x.residentialUnitGroupId === deadline.residentialUnitGroupId
        );
        const residentialUnitId = unit?.residentialUnitId;

        if (!residentialUnitId || !projectDeadlineId) {
            return;
        }
        await revertUnitDeadlines(residentialUnitId, projectDeadlineId);
        //TODO: fix display
        Toast.success(<Trans>Reverted successfully</Trans>);
        await handleOpen();
    };

    const handleAccept = async (e) => {
        if (!combinedDeadlines?.residentialUnitGroupDeadlines) {
            return;
        }

        setSaving(true);
        await saveComplexProjectDeadline(projectId, combinedDeadlines.residentialUnitGroupDeadlines);
        setSaving(false);
        Toast.success(<Trans>Saved deadlines</Trans>);
        triggerHandler(onAccept);
        setShowFloors(false);
        setShowUnits(false);
    };

    const handleChanges = (data: ResidentialUnitGroupAndUnitDeadlines) => {
        setCombinedDeadlines(data);
    };

    const handleOpen = async () => {
        setSaving(false);

        if (!projectDeadlineId) {
            return;
        }

        const residentalUnitDeadlines = await GetResidentialUnitsDeadlinesForProjectDeadline(projectDeadlineId);
        const allResidentalUnitGroupsDeadlines = await getComplexProjectDeadlines(projectId);
        const residentalUnitGroupsDeadlines = allResidentalUnitGroupsDeadlines.filter(
            (x) => x.projectDeadlineId === projectDeadlineId
        );
        setCombinedDeadlines({
            residentalUnitDeadlines: residentalUnitDeadlines,
            residentialUnitGroupDeadlines: residentalUnitGroupsDeadlines,
        });
        if (residentalUnitDeadlines.length === 0) {
            setShowUnits(false);
        }
    };

    return (
        <Dialog
            open={show}
            onOpenChange={(open) => {
                if (!open) handleCancelClick();
            }}
            onOpen={handleOpen}>
            <Dialog.Content contentWidth="lg">
                <Dialog.Header>
                    <Trans>Edit deadlines by floor or unit group</Trans>
                </Dialog.Header>
                <AlertBanner type="info">
                    <Trans>
                        <strong>Edit deadlines by floor or unit group </strong>
                        enables you to change the date of a deadline for an individual residential unit group or floor. To
                        ensure the date is updated remember to click on ‘Save changes’.
                    </Trans>
                </AlertBanner>
                <div className={theme.checkbox}>
                    <Checkbox checked={showFloors} onChange={() => setShowFloors(!showFloors)}>
                        {<Trans>Show floors</Trans>}
                    </Checkbox>

                    <Checkbox
                        disabled={
                            combinedDeadlines?.residentalUnitDeadlines !== undefined &&
                            combinedDeadlines?.residentalUnitDeadlines !== null &&
                            combinedDeadlines?.residentalUnitDeadlines.length < 1
                        }
                        checked={showUnits}
                        onChange={() => setShowUnits(!showUnits)}>
                        <>
                            {<Trans>Show units with overrides</Trans>}
                            {combinedDeadlines?.residentalUnitDeadlines && (
                                <NotificationBadge
                                    className={theme.notification}
                                    value={combinedDeadlines?.residentalUnitDeadlines.length}
                                />
                            )}
                        </>
                    </Checkbox>
                </div>
                {combinedDeadlines ? (
                    <ProjectSettingsComplexDeadlinesTable
                        data={combinedDeadlines}
                        showFloors={showFloors}
                        showUnits={showUnits}
                        onChange={handleChanges}
                        onRevertUnitGroup={(x) => handleRevertRugClick(x)}
                        onRevertUnit={(x) => handleRevertRuClick(x)}
                    />
                ) : (
                    <LoadingIndicator />
                )}
                <ButtonBar>
                    <ButtonBar.Right>
                        <Button variant="tertiary" onClick={handleCancelClick} disabled={saving}>
                            <Trans>Cancel</Trans>
                        </Button>
                        <Button variant="primary" onClick={handleAccept} loading={saving}>
                            <Trans>Save changes</Trans>
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
            </Dialog.Content>
        </Dialog>
    );
};

export default ProjectSettingsComplexDeadlines;
